import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  Stack,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Delete as DeleteIcon,
  Person as DoctorIcon,
  CalendarMonth as DateIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { ja } from 'date-fns/locale';
import { motion } from 'framer-motion';
import type { DoctorShift } from '../../types/doctorShift';

interface DoctorShiftDeleteDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<boolean>;
  shift: DoctorShift | null;
  isLoading?: boolean;
}

/**
 * 医師シフト削除確認ダイアログコンポーネント
 * 
 * 意図：
 * - 誤操作防止のための確認ダイアログ
 * - 削除対象の詳細情報表示
 * - 明確な操作フィードバック
 * 
 * 注意点：
 * - 削除の不可逆性を明示
 * - エラーハンドリング
 * 
 * 改善提案：
 * - 関連データの影響範囲表示
 * - 一括削除対応
 * - 削除理由の記録機能
 */
export const DoctorShiftDeleteDialog: React.FC<DoctorShiftDeleteDialogProps> = ({
  open,
  onClose,
  onConfirm,
  shift,
  isLoading = false,
}) => {
  const theme = useTheme();

  if (!shift) return null;

  const handleConfirm = async () => {
    const success = await onConfirm();
    if (success) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: `0 20px 40px ${alpha(theme.palette.common.black, 0.15)}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          p: 3,
          backgroundColor: alpha(theme.palette.error.main, 0.05),
          borderBottom: `1px solid ${alpha(theme.palette.error.main, 0.1)}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <WarningIcon color="error" />
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            医師シフト削除の確認
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Stack spacing={3}>
            {/* 警告メッセージ */}
            <Alert 
              severity="warning" 
              sx={{ 
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.warning.main, 0.1),
              }}
            >
              <Typography variant="body2">
                以下の医師シフトを削除します。<br />
                <strong>この操作は取り消すことができません。</strong>
              </Typography>
            </Alert>

            {/* 削除対象の詳細 */}
            <Box
              sx={{
                p: 2,
                backgroundColor: alpha(theme.palette.grey[500], 0.05),
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
              }}
            >
              <Stack spacing={2}>
                {/* 医師ID */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <DoctorIcon color="action" fontSize="small" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      医師ID
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {shift.doctor_id}
                    </Typography>
                  </Box>
                </Box>

                {/* シフト日付 */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <DateIcon color="action" fontSize="small" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      シフト日付
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {format(parseISO(shift.shift_date), 'yyyy年MM月dd日 (EEEE)', { locale: ja })}
                    </Typography>
                  </Box>
                </Box>

                {/* 勤務時間 */}
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    勤務時間
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {shift.start_time} ～ {shift.end_time}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            {/* 最終確認メッセージ */}
            <Box
              sx={{
                p: 2,
                backgroundColor: alpha(theme.palette.error.main, 0.05),
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
              }}
            >
              <Typography variant="body2" color="error" sx={{ fontWeight: 600 }}>
                本当にこの医師シフトを削除しますか？
              </Typography>
            </Box>
          </Stack>
        </motion.div>
      </DialogContent>

      <DialogActions 
        sx={{ 
          p: 3, 
          backgroundColor: alpha(theme.palette.background.default, 0.5),
          gap: 1
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={isLoading}
          sx={{ borderRadius: 2 }}
        >
          キャンセル
        </Button>
        
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          disabled={isLoading}
          startIcon={isLoading ? undefined : <DeleteIcon />}
          sx={{ borderRadius: 2, minWidth: 120 }}
        >
          {isLoading ? '削除中...' : '削除する'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};